import { useState, useRef, useEffect, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, Shield, Clock, DollarSign, ArrowRight, Eye, EyeOff, UserCheck, Phone, Mail } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import LawFirmTypeahead from '@/components/LawFirmTypeahead';
import AttorneyTypeahead from '@/components/AttorneyTypeahead';
import { splitAttorneyName } from '@/lib/attorneyName';
import { clearDraft, hasDraftContent, readDraft, writeDraft } from '@/lib/applyDraft';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import useApplyLead from '@/hooks/useApplyLead';
import shared from '@/styles/public.module.css';
// Imported as formStyles, not form: the step components take a `form` prop
// (the application state), which would shadow it inside their bodies.
import formStyles from '@/styles/form.module.css';
import styles from './ApplyNow.module.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** How long the logged-in check may take before /apply gives up and shows
 *  the form anyway. See the fail-open note on the effect that uses it. */
const AUTH_CHECK_TIMEOUT_MS = 5000;

function useScrollToTop() { useEffect(() => { window.scrollTo(0, 0); }, []); }

const CASE_TYPES = [
  { value: 'auto_accident', label: 'Auto Accident / MVA' },
  { value: 'rideshare_accident', label: 'Ride Share Auto Accident' },
  { value: 'slip_fall', label: "Slip and Fall / Injury on Other's Property" },
  { value: 'dog_bite', label: 'Dog Bite' },
  { value: 'medical_malpractice', label: 'Medical Malpractice' },
  { value: 'other', label: 'Other' }
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY'
];

const formatCurrencyInput = (raw) => {
  // Strip everything except digits, format with commas. Keeps the field
  // friendly to paste-from-bank-account "$10,000.00" style inputs.
  const digits = (raw || '').toString().replace(/[^0-9]/g, '').slice(0, 9);
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const ErrorText = ({ msg }) => (msg ? <p className={formStyles.error}>{msg}</p> : null);

/** Shared input class: base control plus the error border when invalid. */
const control = (hasError) =>
  `${formStyles.control}${hasError ? ` ${formStyles.controlError}` : ''}`;

/* ---- Step Components ---- */
const StepOne = ({ form, setForm, errors }) => (
  <div data-testid="step-one">
    {/* Amount leads: it is the one field the applicant actually came here
        about. It used to sit on step 2 between attorney email and firm
        phone, framed as paperwork. */}
    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-amount">Funding Amount Needed *</label>
      <div className={formStyles.adorned}>
        <span className={formStyles.currencyMark} aria-hidden="true">$</span>
        <input
          id="apply-amount"
          className={`${control(errors.requestedAmount)} ${formStyles.withIcon} mcl-num`}
          data-testid="input-requested-amount"
          type="text"
          inputMode="numeric"
          value={form.requestedAmount}
          onChange={e => setForm(f => ({ ...f, requestedAmount: formatCurrencyInput(e.target.value) }))}
          placeholder="e.g. 5,000"
        />
      </div>
      <p className={formStyles.hint}>How much cash you&apos;d like to receive now. Most advances are $500-$100,000.</p>
      <ErrorText msg={errors.requestedAmount} />
    </div>

    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-case-type">Type of Accident *</label>
      <Select value={form.caseType} onValueChange={(v) => setForm(f => ({...f, caseType: v}))}>
        <SelectTrigger id="apply-case-type" data-testid="select-case-type" className={control(errors.caseType)}>
          <SelectValue placeholder="Select accident type" />
        </SelectTrigger>
        <SelectContent>{CASE_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
      </Select>
      <ErrorText msg={errors.caseType} />
    </div>

    <div className={formStyles.field}>
      {/* Phrased as a question, matching the client dashboard. "State of
          Accident" read as though it might mean the applicant's own state. */}
      <label className={formStyles.label} htmlFor="apply-accident-state">In what state did the accident occur? *</label>
      <Select value={form.accidentState} onValueChange={(v) => setForm(f => ({...f, accidentState: v}))}>
        <SelectTrigger id="apply-accident-state" data-testid="select-accident-state" className={control(errors.accidentState)}>
          <SelectValue placeholder="Select state" />
        </SelectTrigger>
        <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <ErrorText msg={errors.accidentState} />
    </div>

    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-email">Email *</label>
      <input id="apply-email" className={control(errors.email)} data-testid="input-email" type="email" autoComplete="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="john@example.com" />
      <ErrorText msg={errors.email} />
    </div>
    {/* Phone stays on step 1. It is the channel used to reach an applicant
        who does not finish, so collecting it last would mean holding a
        number only for the people who did finish. */}
    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-phone">Phone *</label>
      <input id="apply-phone" className={control(errors.phone)} data-testid="input-phone" type="tel" autoComplete="tel" value={form.phone} onChange={e => setForm(f => ({...f, phone: formatPhone(e.target.value)}))} placeholder="(555) 123-4567" />
      <p className={formStyles.hint}>So we can reach you about your offer.</p>
      <ErrorText msg={errors.phone} />
    </div>
  </div>
);

const StepTwo = ({ form, setForm, errors, firmFromMatch, setFirmFromMatch }) => (
  <div data-testid="step-two">
    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-attorney-name">Attorney Name *</label>
      <AttorneyTypeahead
        value={form.attorneyName}
        selectedId={form.attorneySelectedId}
        hasError={!!errors.attorneyName}
        // lawFirmPhone is still in the typeahead's payload; /apply no longer
        // collects it, so it is deliberately not destructured here.
        onChange={({ user_id, fullName, lawFirmName, firm_id }) => {
          // Decided HERE, at match time, and never recomputed from the current
          // value. A condition like `attorneySelectedId && lawFirmName` would
          // become true on the applicant's first keystroke and hide the field
          // mid-edit. See the note on LawFirmTypeahead below.
          setFirmFromMatch(!!(user_id && lawFirmName && lawFirmName.trim()));
          setForm(f => ({
            ...f,
            attorneyName: fullName,
            attorneySelectedId: user_id,
            // Only overwrite firm details on a real match; typing must not
            // wipe a firm the applicant already entered by hand.
            lawFirmName: user_id ? (lawFirmName || f.lawFirmName) : f.lawFirmName,
            // A match's firm_id must win over whatever firm_id the applicant
            // had picked earlier in the firm typeahead — otherwise the app
            // files under the old firm while displaying the matched lawyer's
            // firm name. Clearing it on a match with no firm is correct: the
            // backend then resolves firm_id by name instead.
            firm_id: user_id ? (firm_id || '') : f.firm_id,
          }));
        }}
      />
      <ErrorText msg={errors.attorneyName} />
    </div>

    {form.attorneySelectedId && (
      <div className={styles.matched} data-testid="attorney-matched">
        <UserCheck size={16} aria-hidden="true" />
        <span>Attorney matched: <strong>{form.attorneyName}</strong>{form.lawFirmName ? ` at ${form.lawFirmName}` : ''}</span>
        {firmFromMatch && (
          <button
            type="button"
            className={styles.matchedChange}
            data-testid="change-firm-btn"
            onClick={() => setFirmFromMatch(false)}
          >
            Change firm
          </button>
        )}
      </div>
    )}

    {!form.attorneySelectedId && (
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="apply-attorney-email">Attorney Email *</label>
        <input id="apply-attorney-email" className={control(errors.attorneyEmail)} data-testid="input-attorney-email" type="email" value={form.attorneyEmail} onChange={e => setForm(f => ({...f, attorneyEmail: e.target.value}))} placeholder="jane@lawfirm.com" />
        <ErrorText msg={errors.attorneyEmail} />
        <p className={formStyles.hint}>We contact your attorney to verify the case — you don&apos;t have to.</p>
      </div>
    )}

    {/* Hidden only when the match actually supplied a firm name — which the
        banner above is already displaying, so asking again is redundant.
        Crucially this is NOT "hidden whenever an attorney is matched": a
        sizeable share of lawyer records carry no firm at all, and those
        applicants must still be asked or the application files with no firm.
        `firmFromMatch` is set once, at match time, for the reason given
        below. "Change firm" clears it and brings this field back. */}
    {!firmFromMatch && (
      <div className={formStyles.field}>
        <label className={formStyles.label}>Law Firm Name *</label>
        <LawFirmTypeahead
          value={form.lawFirmName}
          firmId={form.firm_id}
          onChange={({ firm_id, name }) => setForm(f => ({ ...f, lawFirmName: name, firm_id }))}
          // Never read-only. A matched attorney auto-fills this as a convenience,
          // but the applicant must be able to correct it: many lawyer records
          // carry no firm name, and the auto-filled one can simply be wrong.
          //
          // Do NOT reintroduce a `readOnly` derived from the current value
          // (e.g. `attorneySelectedId && lawFirmName`). That locks the field on
          // the first character the applicant types, because typing is what
          // makes the condition true — the field ends up frozen holding one
          // character. Any future lock must be decided when the match happens,
          // not recomputed from the value on every render.
          hasError={!!errors.lawFirmName}
          placeholder="Smith & Associates"
          inputTestId="input-firm-name"
        />
        <ErrorText msg={errors.lawFirmName} />
      </div>
    )}
  </div>
);

const StepThree = ({
  form, setForm, errors,
  password, setPassword, confirmPassword, setConfirmPassword,
  showPassword, setShowPassword, accountExists,
}) => (
  <div data-testid="step-three">
    {/* Two modes. A returning applicant signs in; everyone else creates an
        account here rather than on a screen after submit, so there is no
        post-submit gate to strand anyone. */}
    {accountExists && (
      <div className={styles.matched} data-testid="account-exists-notice">
        <UserCheck size={16} aria-hidden="true" />
        <span>We noticed you already have an account. Sign in and we&apos;ll add this request to it.</span>
      </div>
    )}

    <div className={`${formStyles.row2} ${formStyles.field}`}>
      <div>
        <label className={formStyles.label} htmlFor="apply-first-name">First Name *</label>
        <input id="apply-first-name" className={control(errors.firstName)} data-testid="input-first-name" autoComplete="given-name" value={form.firstName} onChange={e => setForm(f => ({...f, firstName: e.target.value}))} placeholder="John" />
        <ErrorText msg={errors.firstName} />
      </div>
      <div>
        <label className={formStyles.label} htmlFor="apply-last-name">Last Name *</label>
        <input id="apply-last-name" className={control(errors.lastName)} data-testid="input-last-name" autoComplete="family-name" value={form.lastName} onChange={e => setForm(f => ({...f, lastName: e.target.value}))} placeholder="Doe" />
        <ErrorText msg={errors.lastName} />
      </div>
    </div>
    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor="apply-password">Password *</label>
      <div className={formStyles.passwordWrap}>
        <input
          id="apply-password"
          className={control(errors.password)}
          data-testid="input-password"
          type={showPassword ? 'text' : 'password'}
          // current-password when signing in, so a password manager offers
          // the saved one instead of proposing a new one.
          autoComplete={accountExists ? 'current-password' : 'new-password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder={accountExists ? 'Your existing password' : 'Min 8 chars, 1 uppercase, 1 number'}
        />
        <button type="button" className={formStyles.passwordToggle} onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="toggle-password">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
      </div>
      {accountExists && (
        <p className={formStyles.hint}>
          <a href="/forgot-password" target="_blank" rel="noopener noreferrer" data-testid="forgot-password-link">
            Forgot your password?
          </a>{' '}
          You can also submit without signing in.
        </p>
      )}
      <ErrorText msg={errors.password} />
    </div>
    {!accountExists && (
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="apply-confirm-password">Confirm Password *</label>
        <input id="apply-confirm-password" className={control(errors.confirmPassword)} data-testid="input-confirm-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" />
        <ErrorText msg={errors.confirmPassword} />
      </div>
    )}
    <div>
      <span className={formStyles.label}>Preferred Contact Method</span>
      <div className={styles.contactOptions}>
        <label className={styles.contactOption}>
          <input type="checkbox" className={formStyles.checkbox} checked={form.contactEmail} onChange={(e) => setForm(f => ({...f, contactEmail: e.target.checked}))} />
          <Mail size={16} aria-hidden="true" /> Email
        </label>
        <label className={styles.contactOption}>
          <input type="checkbox" className={formStyles.checkbox} checked={form.contactSMS} onChange={(e) => setForm(f => ({...f, contactSMS: e.target.checked}))} />
          <Phone size={16} aria-hidden="true" /> SMS / Text
        </label>
      </div>
    </div>
  </div>
);

/* ---- Progress Indicator ---- */
const FormProgress = ({ step, totalSteps }) => (
  <div className={styles.progress} data-testid="form-progress">
    {Array.from({ length: totalSteps }, (_, i) => (
      <Fragment key={i}>
        <div className={`${styles.dot} ${i < step ? styles.dotDone : i === step ? styles.dotActive : ''}`}>
          {i < step ? <Check size={16} aria-hidden="true" /> : i + 1}
        </div>
        {i < totalSteps - 1 && (
          <div className={`${styles.line} ${i < step ? styles.lineDone : ''}`} />
        )}
      </Fragment>
    ))}
  </div>
);

const STEP_TITLES = ['Your Case', 'Your Attorney', 'Create Your Account'];

const HERO_PILLS = [
  { icon: Shield, text: 'No Upfront Fees' },
  { icon: Check, text: 'No Credit Check' },
  { icon: DollarSign, text: 'Only Pay If You Win' },
];

const TRUST_CARDS = [
  { icon: Clock, title: 'Fast Approvals', desc: 'Most applications reviewed within 24 hours' },
  { icon: DollarSign, title: '$500 - $100,000+', desc: 'Funding available based on your case value' },
  { icon: Shield, title: 'Zero Risk', desc: "If you don't win your case, you owe nothing" },
];

const HOW_IT_WORKS = [
  { num: '1', title: 'Apply Online', desc: 'Fill out our simple 3-step form with your case details.' },
  { num: '2', title: 'Quick Review', desc: 'We review your application and consult with your attorney.' },
  { num: '3', title: 'Get Funded', desc: 'Receive your funds in as little as 24 hours once approved.' },
];

/* ---- Main Component ---- */
export default function ApplyNow() {
  usePageMeta(PAGE_META.apply);

  useScrollToTop();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  // Auth gate: if the visitor already has a working session, redirect them to
  // their dashboard instead of letting them create a second/duplicate
  // application from the public form. This is the most common cause of the
  // "user submitted apply-now twice and ended up with two applications" bug.
  // Resolved synchronously when there is no token, because there is nothing to
  // check in that case. Starting false and flipping it in the effect made the
  // first render show the gate while the prerendered markup showed the form,
  // which failed hydration on this route and made React throw the prerendered
  // DOM away. A visitor holding a token still starts unresolved and waits for
  // the round trip below.
  const readToken = () => ((typeof window !== 'undefined') ? localStorage.getItem('access_token') : null);
  const [authChecked, setAuthChecked] = useState(() => !readToken());
  useEffect(() => {
    const token = readToken();
    if (!token) { setAuthChecked(true); return undefined; }

    // This gate FAILS OPEN, on a timeout. It exists only to spare a
    // logged-in visitor a flash of the form; it must never be able to
    // withhold /apply from an applicant, which is the whole conversion path.
    //
    // A backend that refuses the connection rejects quickly and lands in
    // .catch below, so an outage is already handled. The gap this closes is
    // a request that is ACCEPTED and then never answered — a stalled proxy,
    // or a dead uvicorn worker whose reloader still holds the socket, which
    // is what happened on 2026-08-12 and left this page on "Loading..."
    // indefinitely with no error and no retry.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AUTH_CHECK_TIMEOUT_MS);

    fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(me => {
        if (me && me.user_id) {
          // Already logged in. Bounce to role-appropriate dashboard.
          const dest = me.role === 'lawyer' ? '/lawyer'
                     : me.role === 'funder' ? '/funder'
                     : me.role === 'admin'  ? '/admin'
                     : '/client';
          navigate(dest, { replace: true, state: { applyNowRedirect: true } });
        } else {
          setAuthChecked(true);
        }
      })
      // Covers the abort as well as a network failure: either way the
      // applicant gets the form rather than a dead end.
      .catch(() => setAuthChecked(true))
      .finally(() => clearTimeout(timer));

    return () => { clearTimeout(timer); controller.abort(); };
  }, [navigate]);

  const [searchParams] = useSearchParams();
  // Case-type landing pages link here as /apply?type=dog_bite. Accept the value
  // only if it is one we actually offer, so a hand-edited or stale URL cannot
  // put an unselectable value into the form.
  const requestedType = searchParams.get('type');
  const initialCaseType = CASE_TYPES.some((c) => c.value === requestedType)
    ? requestedType
    : '';

  // /apply is a deliberately partial application: it asks only what is needed
  // to create the account, route to the attorney, and qualify the case. The
  // street address, law-firm phone and date of incident it used to collect are
  // all chased by the client dashboard after login, so they are not here.
  const [form, setForm] = useState({
    caseType: initialCaseType, accidentState: '', firstName: '', lastName: '', email: '', phone: '',
    attorneyName: '', attorneyEmail: '', attorneySelectedId: '',
    lawFirmName: '', firm_id: '',
    fundingAmountRange: '', requestedAmount: '',
    contactEmail: true, contactSMS: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const [attorneyNotified, setAttorneyNotified] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const submittedRef = useRef(false);
  const [draftRestored, setDraftRestored] = useState(false);
  // True only when an attorney match supplied a firm name. Set at match time
  // in StepTwo, never derived from the current field value.
  const [firmFromMatch, setFirmFromMatch] = useState(false);
  // Answered by the step-1 lead ping. Drives whether step 3 asks somebody to
  // sign in or to create an account. Defaults false, so any failure of that
  // lookup lands on the create-account form, which works for everyone.
  const [accountExists, setAccountExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const sendLead = useApplyLead(API, form, step, submittedRef);

  // Restore in an effect, NEVER in a useState initializer.
  //
  // /apply is prerendered (frontend/scripts/prerender.js). Reading
  // localStorage during initial state would make the prerendered HTML — an
  // empty form — disagree with the first client render — a filled one.
  // That is a hydration mismatch, and React responds by throwing the
  // prerendered DOM away. This is not hypothetical: the comment on
  // authChecked above documents exactly this failure on exactly this route.
  useEffect(() => {
    const draft = readDraft();
    // An empty draft is not progress. Restoring one would announce "we saved
    // your progress" to a first-time visitor who has typed nothing.
    if (!draft || !hasDraftContent(draft.form)) return;
    setForm((f) => ({
      ...f,
      ...draft.form,
      // An explicit ?type= from a case-type landing page is a fresh, stated
      // intent and outranks whatever a week-old draft happened to hold.
      caseType: initialCaseType || draft.form.caseType || f.caseType,
    }));
    if (typeof draft.step === 'number') setStep(draft.step);
    setDraftRestored(true);
    // Runs once on mount. initialCaseType is derived from the URL, which
    // does not change without a remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced so a fast typist does not write on every keystroke.
  //
  // Skipped entirely while the form is pristine. Writing an empty draft is
  // what made a first visit to /apply announce restored progress on the
  // next one. Skipping rather than clearing is deliberate: this effect also
  // runs on mount, before the restore above has been applied to state, so
  // clearing here would wipe the draft it is about to restore.
  useEffect(() => {
    if (submittedRef.current) return undefined;
    if (!hasDraftContent(form)) return undefined;
    const timer = setTimeout(() => writeDraft(form, step), 500);
    return () => clearTimeout(timer);
  }, [form, step]);

  const validate = (s) => {
    const e = {};
    if (s === 0) {
      const amt = parseFloat((form.requestedAmount || '').toString().replace(/[^0-9.]/g, ''));
      if (!form.requestedAmount || isNaN(amt) || amt <= 0) e.requestedAmount = 'Enter the amount you need';
      else if (amt < 500) e.requestedAmount = 'Minimum advance is $500';
      else if (amt > 1000000) e.requestedAmount = 'Maximum advance is $1,000,000';
      if (!form.caseType) e.caseType = 'Required';
      if (!form.accidentState) e.accidentState = 'Required';
      if (!form.email.trim()) e.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
      if (!form.phone.trim()) e.phone = 'Required';
      else if (form.phone.replace(/\D/g, '').length < 10) e.phone = 'Enter a valid 10-digit number';
    }
    else if (s === 1) {
      if (!form.attorneyName.trim()) e.attorneyName = 'Required';
      // Still required, but the error is attached to the attorney field when
      // the firm input is hidden — an error message rendered next to a field
      // nobody can see is an unfixable dead end.
      if (!form.lawFirmName.trim()) {
        if (firmFromMatch) e.attorneyName = 'This attorney has no law firm on record — pick a different attorney or enter the firm.';
        else e.lawFirmName = 'Required';
      }
      // Attorney email is only needed when no real attorney was linked — the
      // backend reads the address off the lawyer record when one is.
      if (!form.attorneySelectedId) {
        if (!form.attorneyEmail || !form.attorneyEmail.trim()) e.attorneyEmail = 'Attorney email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.attorneyEmail)) e.attorneyEmail = 'Invalid email';
      }
    }
    else if (s === 2) {
      if (!form.firstName.trim()) e.firstName = 'Required';
      if (!form.lastName.trim()) e.lastName = 'Required';
      if (accountExists) {
        // Signing in, not registering. The complexity rules must NOT apply —
        // they describe what a new password has to look like, and an existing
        // one was set under whatever rules were in force at the time.
        if (!password) e.password = 'Enter your password, or submit without signing in';
      } else {
        // Mirrors the rules /apply/set-password enforces server-side, so the
        // applicant is told here rather than after the application is filed.
        if (!password) e.password = 'Required';
        else if (password.length < 8) e.password = 'Password must be at least 8 characters';
        else if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) e.password = 'Include at least one uppercase letter and one number';
        if (!confirmPassword) e.confirmPassword = 'Required';
        else if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match';
      }
    }
    setErrors(e); return Object.keys(e).length === 0;
  };

  const advance = () => { setStep(s => s + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const next = async () => {
    if (!validate(step)) return;

    // Leaving step 1 is the only place the answer matters, because step 3 is
    // where it changes what is asked. Elsewhere the capture stays
    // fire-and-forget. Called before advancing because the lead's depth is
    // the step just completed.
    if (step === 0) {
      setCheckingEmail(true);
      try {
        const answer = await sendLead({ wantAnswer: true });
        setAccountExists(!!(answer && answer.account_exists));
      } catch (err) {
        // Never blocks. A failed lookup means the create-account form, which
        // is correct for a new applicant and merely suboptimal for a
        // returning one — POST /apply still links by email either way.
        setAccountExists(false);
      } finally {
        setCheckingEmail(false);
      }
      advance();
      return;
    }

    sendLead();
    advance();
  };
  const prev = () => { setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  /** Sign a returning applicant in BEFORE the application is filed.
   *
   *  Order matters. Filing first and authenticating second would, on a wrong
   *  password, leave an application the person cannot see and cannot cleanly
   *  retry. This way a failed attempt leaves nothing behind.
   *
   *  Returns true on success. On failure it sets the error and returns false;
   *  the caller decides whether to continue without a session.
   */
  const signInExisting = async () => {
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: form.email.trim().toLowerCase(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrors(e => ({
          ...e,
          password: res.status === 429
            ? (data.detail || 'Too many attempts. Wait a few minutes, or submit without signing in.')
            : 'That password did not match. Try again, reset it, or submit without signing in.',
        }));
        return false;
      }
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      return true;
    } catch (err) {
      setErrors(e => ({ ...e, password: 'Could not sign you in. You can submit without signing in.' }));
      return false;
    }
  };

  /** @param {{skipSignIn?: boolean}} opts */
  const handleSubmit = async (opts = {}) => {
    if (!validate(2)) return; if (submittedRef.current) return;

    // Sign in first, and abort the whole submission if it fails, unless the
    // applicant explicitly chose to continue without it. Never a hard gate:
    // a forgotten password must not cost somebody their application.
    if (accountExists && !opts.skipSignIn) {
      setSubmitting(true);
      const ok = await signInExisting();
      setSubmitting(false);
      if (!ok) return;
    }

    submittedRef.current = true; setSubmitting(true); setApiError('');
    try {
      // Strip commas + coerce to plain number before sending.
      const numericAmount = parseFloat((form.requestedAmount || '').toString().replace(/[^0-9.]/g, '')) || 0;
      const { firstName: attorneyFirstName, lastName: attorneyLastName } = splitAttorneyName(form.attorneyName);
      // POST /api/apply requires the name as two separate required strings, so
      // the single input is split here rather than changing the API contract.
      const { attorneyName, ...rest } = form;
      const payload = { ...rest, attorneyFirstName, attorneyLastName, requestedAmount: numericAmount };
      const res = await fetch(`${API}/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.detail || 'Submission failed'); }
      const data = await res.json();
      setAttorneyNotified(data.attorney_notified || false);
      setExistingUser(data.existing_user || false);
      // The application exists server-side now; the draft is spent.
      clearDraft();

      // An address that already has an account cannot have a password set
      // through this endpoint. If they signed in above we already hold a
      // token, so take them straight to the application they just filed.
      // Otherwise they get the confirmation screen and a link to log in.
      if (data.existing_user) {
        const signedIn = accountExists && !opts.skipSignIn;
        if (signedIn && data.application_id) {
          navigate(`/client/application/${data.application_id}`, { state: { justCompletedApply: true }, replace: true });
          return;
        }
        if (signedIn) { navigate('/client', { replace: true }); return; }
        setSubmitted(true);
        return;
      }

      // Otherwise create the account straight away and drop them into it.
      // This is why there is no password screen after submit any more: the
      // two calls run seconds apart, so nobody is left with an application
      // they cannot reach. If the account step fails the application is
      // still filed, so fall back to the confirmation screen, which carries
      // a password form they can retry from.
      try {
        await createAccount();
      } catch (err) {
        setPasswordError(err.message);
        setSubmitted(true);
      }
    } catch (err) { setApiError(err.message); submittedRef.current = false; }
    finally { setSubmitting(false); }
  };

  /** POST /apply/set-password, store the token, and navigate into the app.
   *  Throws on failure so the caller decides what the applicant sees. */
  const createAccount = async () => {
    const res = await fetch(`${API}/apply/set-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: form.email, password }) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to create account');
    localStorage.setItem('access_token', data.access_token);
    // Drop the new client directly into the application they just submitted
    // so they can finish completing it — /apply is deliberately partial, and
    // the dashboard is where the rest is collected. The destination page
    // shows an onboarding banner driven by location.state.justCompletedApply.
    if (data.application_id) {
      navigate(`/client/application/${data.application_id}`, { state: { user: data.user, justCompletedApply: true }, replace: true });
    } else {
      navigate('/client', { state: { user: data.user }, replace: true });
    }
  };

  /** Retry path only. The account is normally created by handleSubmit; this
   *  backs the password form on the confirmation screen, which is shown when
   *  that first attempt failed. */
  const handleSetPassword = async () => {
    setSettingPassword(true); setPasswordError('');
    try {
      await createAccount();
    } catch (err) { setPasswordError(err.message); }
    finally { setSettingPassword(false); }
  };

  /* ---- Auth check gate (prevents form flash for already-logged-in users) ---- */
  if (!authChecked) {
    return (
      <div className={styles.page}>
        <LandingHeader />

        <main className={styles.loading}>
          <p>Loading...</p>
        </main>
      </div>
    );
  }

  /* ---- Confirmation / Password Screen ---- */
  if (submitted) {
    return (
      <div className={styles.page}>
        <LandingHeader />

      <main>
        <section className={shared.section}>
          <div className={`${shared.wrap} ${styles.confirmWrap}`}>
            <div className={styles.confirmCard} data-testid="confirmation-screen">
              <div className={styles.statusIcon}>
                <Check size={32} aria-hidden="true" />
              </div>
              <h2 className={styles.confirmTitle}>Request Submitted!</h2>
              <p className={styles.confirmLead}>A representative will contact you shortly.</p>
              {attorneyNotified && (
                <div className={styles.banner} data-testid="attorney-notified-msg">
                  Your attorney has been notified and will need to review your application.
                </div>
              )}
              {existingUser ? (
                <div data-testid="existing-user-section">
                  <p className={styles.confirmLead}>Your application has been added to your existing account. Log in to view the status and review funding offers.</p>
                  <button type="button" className={`${shared.btn} ${shared.btnAction} ${shared.btnLg}`} onClick={() => navigate('/login')} data-testid="login-redirect-btn">Log In to View Application</button>
                </div>
              ) : (
                <>
                  <p className={styles.confirmPrompt}>Create a password to see your offers!</p>
                  <div className={styles.confirmForm}>
                    <div className={formStyles.field}>
                      <label className={formStyles.label} htmlFor="apply-password">Password *</label>
                      <div className={formStyles.passwordWrap}>
                        <input id="apply-password" className={formStyles.control} data-testid="input-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number" />
                        <button type="button" className={formStyles.passwordToggle} onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                      </div>
                    </div>
                    <div className={formStyles.field}>
                      <label className={formStyles.label} htmlFor="apply-confirm-password">Confirm Password *</label>
                      <input id="apply-confirm-password" className={formStyles.control} data-testid="input-confirm-password" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" />
                    </div>
                    {passwordError && <p className={formStyles.error}>{passwordError}</p>}
                    <button type="button" className={`${shared.btn} ${shared.btnAction} ${shared.btnLg}`} onClick={handleSetPassword} disabled={settingPassword} data-testid="create-account-btn">
                      {settingPassword ? 'Creating...' : 'Create Account & View Portal'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

        <LandingFooter />
      </div>
    );
  }

  /* ---- Main Page ---- */
  return (
    <div className={styles.page} data-testid="apply-now-page">
      <LandingHeader />

      <main>

      {/* Hero + form */}
      <section className={styles.band}>
        <div className={`${shared.wrap} ${styles.bandGrid}`}>
          {/* Left: hero copy */}
          <div data-testid="hero-section">
            <h1 className={styles.heroTitle}>
              Get the Cash You Need <em>Before</em> Your Case Settles
            </h1>
            <p className={styles.heroSub}>
              Apply in under 2 minutes. No credit check. No upfront fees. You only pay if you win.
            </p>
            <ul className={styles.pills}>
              {HERO_PILLS.map(({ icon: Icon, text }) => (
                <li key={text} className={styles.pill}>
                  <Icon size={16} aria-hidden="true" /> {text}
                </li>
              ))}
            </ul>

            <div className={styles.trustCards}>
              {TRUST_CARDS.map((item) => (
                <div key={item.title} className={styles.trustCard}>
                  <div className={styles.trustIcon}>
                    <item.icon size={22} aria-hidden="true" />
                  </div>
                  <div>
                    <div className={styles.trustTitle}>{item.title}</div>
                    <div className={styles.trustDesc}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form card */}
          <div>
            <div className={styles.card} data-testid="application-form-card">
              <div className={styles.cardHead}>
                <span className={styles.badge}>Takes less than 2 minutes</span>
                <h2 className={styles.cardTitle}>Start Your Application</h2>
              </div>

              {draftRestored && (
                <div className={styles.restored} data-testid="draft-restored-notice">
                  <Check size={16} aria-hidden="true" />
                  <span>We saved your progress. Everything you had filled in is still here.</span>
                  <button
                    type="button"
                    className={styles.restoredReset}
                    data-testid="draft-start-over"
                    onClick={() => {
                      clearDraft();
                      window.location.reload();
                    }}
                  >
                    Start over
                  </button>
                </div>
              )}

              <FormProgress step={step} totalSteps={3} />

              <p className={styles.stepLabel}>
                Step {step + 1}: {STEP_TITLES[step]}
              </p>

              {step === 0 && <StepOne form={form} setForm={setForm} errors={errors} />}
              {step === 1 && (
                <StepTwo
                  form={form} setForm={setForm} errors={errors}
                  firmFromMatch={firmFromMatch} setFirmFromMatch={setFirmFromMatch}
                />
              )}
              {step === 2 && (
                <StepThree
                  form={form} setForm={setForm} errors={errors}
                  password={password} setPassword={setPassword}
                  confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
                  showPassword={showPassword} setShowPassword={setShowPassword}
                  accountExists={accountExists}
                />
              )}

              {apiError && (
                <div className={styles.apiError} data-testid="api-error">
                  {apiError}
                </div>
              )}

              <div className={styles.actions}>
                {step > 0 && (
                  <button type="button" className={`${shared.btn} ${shared.btnQuiet}`} onClick={prev} data-testid="prev-step-btn">
                    <ChevronLeft size={16} aria-hidden="true" /> Back
                  </button>
                )}
                {step < 2 ? (
                  <button type="button" className={`${shared.btn} ${shared.btnAction} ${styles.grow}`} onClick={next} disabled={checkingEmail} data-testid="next-step-btn">
                    {checkingEmail ? 'Checking...' : <>Continue <ChevronRight size={16} aria-hidden="true" /></>}
                  </button>
                ) : (
                  <button type="button" className={`${shared.btn} ${shared.btnAction} ${styles.grow}`} onClick={() => handleSubmit()} disabled={submitting} data-testid="submit-application-btn">
                    {submitting
                      ? (accountExists ? 'Signing you in...' : 'Creating your account...')
                      : <>{accountExists ? 'Sign In & Submit' : 'Submit & Create Account'} <ArrowRight size={16} aria-hidden="true" /></>}
                  </button>
                )}
              </div>

              {/* Sign-in is offered, never required. A forgotten password
                  must not cost somebody their application — the backend
                  links it to their account by email either way. */}
              {step === 2 && accountExists && (
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnQuiet} ${styles.grow}`}
                  onClick={() => handleSubmit({ skipSignIn: true })}
                  disabled={submitting}
                  data-testid="submit-without-signin-btn"
                >
                  Submit without signing in
                </button>
              )}

              {/* New tab, matching login and the signup wizard. This is the
                  costliest place to navigate away from: the application form
                  holds its answers in memory, so reading the terms before
                  submitting would discard everything entered so far. */}
              <p className={styles.legal}>
                {/* "By continuing", not "By submitting". This block renders on
                    every step, but the old wording tied agreement to an act
                    that someone who abandons the form never performs — which
                    is exactly the person §4 of the Terms now covers. */}
                By continuing, you agree to our{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Terms of Service (opens in a new tab)"
                >
                  Terms of Service
                </a>{' '}and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Privacy Policy (opens in a new tab)"
                >
                  Privacy Policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={`${shared.section} ${shared.sectionPanel}`}>
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Simple Process</span>
            <h2>How It Works</h2>
          </div>
          <div className={styles.steps}>
            {HOW_IT_WORKS.map(item => (
              <div key={item.num} className={styles.stepItem}>
                <div className={styles.stepNum}>{item.num}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile sticky CTA */}
      <div className={styles.stickyCta}>
        <button type="button" className={`${shared.btn} ${shared.btnAction} ${shared.btnLg}`} onClick={() => document.querySelector('[data-testid="application-form-card"]')?.scrollIntoView({ behavior: 'smooth' })} data-testid="mobile-cta-btn">
          Start Your Application <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
      </main>


      <LandingFooter />
    </div>
  );
}
